import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { Plus, Smartphone, Ticket } from 'lucide-react'
import { useState } from 'react'
import { onlineOrdersQuery } from '@/api/online-sale'
import { clientName, clientNamesQuery, SOLD_PAGE_SIZE, soldQuery, type IssuedPass } from '@/api/passes'
import { useCompanyCaller } from '@/app/company'
import { NothingForRole } from '@/app/nothing-for-role'
import { AppTag } from '@/app/passes/app-tag'
import { OnlineOrdersPanel } from '@/app/passes/online-orders'
import { PassPanel, SellPanel, StatusBadge } from '@/app/passes/pass-panels'
import { PassesTabs } from '@/app/passes/passes-tabs'
import { StateFilter } from '@/ui/state-filter'
import { errorText } from '@/domain/errors'
import { FILTERS, passDates, passVisits, type PassFilter } from '@/domain/issued-passes'
import { canSeeOnlinePayment, isOnlineSale, ORDER_FILTERS, type OrderFilter } from '@/domain/online-sale'
import { canSeePasses, canSeeSold, canSellPasses, passesLanding } from '@/domain/pass-abilities'
import { t } from '@/i18n'
import { Button } from '@/ui/button'
import { Avatar } from '@/ui/compact-row'
import { DataList, type DataColumn } from '@/ui/data-list'
import { InlineBanner } from '@/ui/inline-banner'
import { EmptyState, Page, PageHeader } from '@/ui/page'
import { LoadingBlock } from '@/ui/spinner'

type SoldSearch = { state?: PassFilter; orders?: OrderFilter }

/// The search with a change applied; a key set to undefined is dropped.
const patchSearch = (prev: SoldSearch, patch: Readonly<Record<keyof SoldSearch, string | undefined>>): SoldSearch =>
  Object.fromEntries(Object.entries({ ...prev, ...patch }).filter(([, value]) => value !== undefined)) as SoldSearch

export const Route = createFileRoute('/_app/c/$orgId/passes/')({
  validateSearch: (search: Record<string, unknown>): SoldSearch => ({
    ...(typeof search['state'] === 'string' && (FILTERS as readonly string[]).includes(search['state'])
      ? { state: search['state'] as PassFilter }
      : {}),
    // The app orders panel, open on a filter (specs/online-sale.md §5).
    ...(typeof search['orders'] === 'string' && (ORDER_FILTERS as readonly string[]).includes(search['orders'])
      ? { orders: search['orders'] as OrderFilter }
      : {}),
  }),
  component: Sold,
})

/// «Абонементи» — the section's first page, since a club sells far more
/// often than it prices: every pass the club sold, by its EFFECTIVE state, a page at a
/// time — the server filters and counts, so the club's ten-thousandth pass is
/// as reachable as its first. Actions on a
/// pass for `passes ≥ edit`; selling for `sales ≥ edit` (the `sell` the
/// server checks). Scope `own` sees no sold passes at all (R8).
function Sold() {
  const { orgId } = Route.useParams()
  const { state = 'all', orders } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { caller } = useCompanyCaller(orgId)
  const allowed = canSeePasses(caller) && canSeeSold(caller)
  const sell = canSellPasses(caller)
  // The app's orders are money matters: `view_finance`, like the server.
  const finance = canSeeOnlinePayment(caller)
  const attention = useQuery({ ...onlineOrdersQuery(orgId, 'attention'), enabled: allowed && finance })
  const needing = attention.data?.length ?? 0
  const passes = useInfiniteQuery({ ...soldQuery(orgId, state), enabled: allowed })
  // Names page by page: each page asks for exactly its own holders.
  const names = useQueries({
    queries: (passes.data?.pages ?? []).map((page) =>
      clientNamesQuery(orgId, [...new Set(page.passes.map((pass) => pass.clientId))].sort()),
    ),
  })
  const [selling, setSelling] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [news, setNews] = useState<string | null>(null)
  const s = t.passes.sold

  if (passesLanding(caller) === 'plans') {
    return <Navigate to="/c/$orgId/passes/plans" params={{ orgId }} replace />
  }
  if (!allowed) return <NothingForRole />

  const list = passes.data?.pages.flatMap((page) => page.passes) ?? []
  const first = passes.data?.pages[0]
  const counts = first?.counts
  const total = first?.total ?? 0
  const known = new Map(names.flatMap((query) => query.data ?? []).map((client) => [client.id, client]))
  const holder = (clientId: string) => {
    const client = known.get(clientId)
    return client ? clientName(client) : s.unknownClient
  }
  const open = openId ? list.find((pass) => pass.id === openId) : undefined

  /// Who · what · state · until when · what is left — the number last and
  /// right-aligned. Expired and revoked rows are greyed: history, not
  /// something to act on today.
  const columns: readonly DataColumn<IssuedPass>[] = [
    {
      key: 'client',
      header: s.columns.client,
      width: 'minmax(0,1.4fr)',
      cell: (pass) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <Avatar name={holder(pass.clientId)} className="size-6 text-[10px] max-sm:hidden" />
          <span className="truncate">{holder(pass.clientId)}</span>
        </span>
      ),
    },
    {
      key: 'plan',
      header: s.columns.plan,
      width: 'minmax(0,1.2fr)',
      // On a phone «Звідки» folds into a glyph after the plan's name.
      cell: (pass) =>
        isOnlineSale(pass) ? (
          <>
            {pass.planName}
            <span className="ml-1.5 inline-flex align-[-4px] sm:hidden">
              <AppTag iconOnly label={`${t.sale.sold.app} · ${t.sale.sold.paidOnline}`} />
            </span>
          </>
        ) : (
          pass.planName
        ),
    },
    {
      // Where it was sold: the desk (the default, quiet) or the app, paid
      // online to the club's merchant (specs/online-sale.md §5).
      key: 'source',
      header: t.sale.sold.source,
      width: '168px',
      mobile: 'hidden',
      cell: (pass) =>
        isOnlineSale(pass) ? (
          <span className="flex min-w-0 items-center gap-1.5" title={`${t.sale.sold.app} · ${t.sale.sold.paidOnline}`}>
            <AppTag label={t.sale.sold.app} />
            <span className="truncate text-caption text-text-2">{t.sale.sold.online}</span>
          </span>
        ) : (
          <span className="text-muted">{t.sale.sold.desk}</span>
        ),
    },
    {
      key: 'status',
      header: s.columns.status,
      width: '136px',
      mobile: 'trailing',
      cell: (pass) => <StatusBadge status={pass.status} variant="dot" />,
    },
    {
      key: 'dates',
      header: s.columns.dates,
      width: 'minmax(0,0.9fr)',
      hideBelow: 'md',
      cell: (pass) => passDates(pass) ?? <span className="text-muted">—</span>,
    },
    {
      key: 'visits',
      header: s.columns.visits,
      width: 'minmax(96px,0.8fr)',
      numeric: true,
      hideBelow: 'lg',
      mobile: 'hidden',
      cell: (pass) => passVisits(pass) ?? <span className="text-muted">{s.unlimited}</span>,
    },
  ]

  const ordersButton = finance ? (
    <Button
      variant="secondary"
      icon={<Smartphone className="size-4" />}
      onClick={() => void navigate({ search: (prev) => patchSearch(prev, { state: prev.state, orders: needing > 0 ? 'attention' : 'all' }), replace: true })}
    >
      {t.sale.orders.open}
      {needing > 0 ? (
        <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-warning px-1.5 text-[11px] font-bold text-white tabular">
          {needing}
        </span>
      ) : null}
    </Button>
  ) : undefined

  const sellButton = sell ? (
    <Button variant="primary" icon={<Plus className="size-[18px]" strokeWidth={2.5} />} onClick={() => {
      setNews(null)
      setSelling(true)
    }}>
      {s.sell}
    </Button>
  ) : undefined

  return (
    <Page wide>
      <PageHeader
        title={t.passes.title}
        subtitle={t.passes.subtitle}
        actions={
          ordersButton || sellButton ? (
            <>
              {ordersButton}
              {sellButton}
            </>
          ) : undefined
        }
        tabs={<PassesTabs orgId={orgId} showSold soldTotal={counts?.all} />}
      />

      {news ? <InlineBanner kind="done" className="mb-6" message={news} /> : null}

      {passes.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : passes.isError && !passes.data ? (
        <InlineBanner kind="error" message={errorText(passes.error)} action={{ label: t.app.retry, onClick: () => void passes.refetch() }} />
      ) : counts?.all === 0 ? (
        <EmptyState icon={<Ticket />} title={s.empty} message={sell ? s.emptyMessage : undefined} action={sellButton} />
      ) : (
        <div className="flex flex-col gap-4">
          <StateFilter
            label={s.filterLabel}
            value={state}
            onChange={(next) =>
              void navigate({ search: (prev) => patchSearch(prev, { state: next === 'all' ? undefined : next, orders: prev.orders }), replace: true })
            }
            options={FILTERS.map((filter) => ({ value: filter, label: s.filters[filter] ?? filter, count: counts?.[filter] }))}
          />
          <DataList
            label={t.passes.tabs.sold}
            columns={columns}
            rows={list}
            rowKey={(pass) => pass.id}
            rowTarget={(pass) => ({ onClick: () => setOpenId(pass.id) })}
            rowLabel={(pass) => `${holder(pass.clientId)}: ${pass.planName}`}
            rowTone={(pass) => (pass.status === 'expired' || pass.status === 'revoked' ? 'muted' : 'normal')}
            empty={s.emptyFilter}
            footer={
              total > list.length || passes.hasNextPage ? (
                <>
                  <span className="tabular">{s.shown(list.length, total)}</span>
                  {passes.hasNextPage ? (
                    <Button
                      compact
                      variant="secondary"
                      busy={passes.isFetchingNextPage}
                      onClick={() => void passes.fetchNextPage()}
                    >
                      {passes.isFetchingNextPage
                        ? s.loadingMore
                        : s.loadMore(Math.min(total - list.length, SOLD_PAGE_SIZE))}
                    </Button>
                  ) : null}
                </>
              ) : undefined
            }
          />
          {passes.isFetchNextPageError ? (
            <InlineBanner kind="error" message={errorText(passes.error)} action={{ label: t.app.retry, onClick: () => void passes.fetchNextPage() }} />
          ) : null}
        </div>
      )}

      {sell ? (
        <SellPanel
          orgId={orgId}
          open={selling}
          onOpenChange={setSelling}
          onSold={(pass, client, plan) => {
            setSelling(false)
            setNews(s.soldBanner(clientName(client), plan.name))
            setOpenId(pass.id)
          }}
        />
      ) : null}
      {finance && orders ? (
        <OnlineOrdersPanel
          orgId={orgId}
          filter={orders}
          onFilter={(next) => void navigate({ search: (prev) => patchSearch(prev, { state: prev.state, orders: next }), replace: true })}
          onClose={() => void navigate({ search: (prev) => patchSearch(prev, { state: prev.state, orders: undefined }), replace: true })}
        />
      ) : null}
      {open ? (
        <PassPanel orgId={orgId} pass={open} holder={holder(open.clientId)} caller={caller} onClose={() => setOpenId(null)} />
      ) : null}
    </Page>
  )
}
